import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/components/ThemeProvider";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, Sun, Moon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/LanguageSelector";
import Logo from "./Logo";

const Navigation = () => {
  const { language, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.about"), path: "/about" },
    { name: t("nav.gallery"), path: "/gallery" },
    { name: t("nav.press"), path: "/press" },
    { name: t("nav.events"), path: "/events" },
    { name: t("nav.final"), path: "/final" },
    { name: t("nav.partners"), path: "/partners" },
    { name: t("nav.faq"), path: "/faq" },
    { name: t("nav.contact"), path: "/contact" },
  ];

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border transition-colors duration-300">
      <div className="container flex items-center justify-between h-16 px-4 md:px-6">
        <div className="flex items-center gap-2">
          <NavLink to="/" className="flex items-center space-x-2">
            <Logo />
          </NavLink>
        </div>

        {/* Desktop Nav - only shown on large screens */}
        <nav className="hidden lg:flex items-center space-x-1 text-sm font-medium">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md transition-colors ${
                  isActive ? "text-primary font-semibold" : "hover:text-primary"
                }`
              }
            >
              {item.name}
            </NavLink>
          ))}
          <NavLink to="/buy-ticket">
            <Button variant="destructive" size="sm" className="ml-2">
              {t("nav.buyTicket")}
            </Button>
          </NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSelector />

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={
              theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"
            }
            className="text-foreground hover:text-primary"
          >
            {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
          </Button>

          {/* Mobile menu button - now shown on medium screens too */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={toggleMenu}
            aria-label="Toggle Menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="lg:hidden bg-background border-b border-border">
          <div className="container py-2">
            <nav className="flex flex-col space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-md transition-colors ${
                      isActive
                        ? "text-primary font-semibold bg-secondary/50"
                        : "hover:bg-secondary/50 hover:text-primary"
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
              <NavLink
                to="/buy-ticket"
                onClick={() => setIsOpen(false)}
                className="px-3 py-2"
              >
                <Button variant="destructive" size="sm" className="w-full">
                  {t("nav.buyTicket")}
                </Button>
              </NavLink>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
